import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/cn';
import {
  fromDate,
  CalendarDate,
  CalendarDateTime,
} from '@internationalized/date';
import type { BaseInstance } from '@pigello/pigello-matrix';
import type { DateValue } from 'react-aria';
import type { Path } from 'react-hook-form';
import type { DatePickerStateOptions } from 'react-stately';
import type { FieldProps } from '../../types';
import { DescriptionTooltip } from '../description-tooltip';
import { DateTimePicker } from './date-time-picker';
import { tryCatch } from '@/utils/try-catch';

type DateFieldProps<Instance extends BaseInstance = BaseInstance> =
  FieldProps<Instance> & DatePickerStateOptions<DateValue>;

export function parse(value: string | null, granularity: string | undefined) {
  if (!value) {
    return null;
  }
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: zonedDate, error } = tryCatch(() =>
    fromDate(new Date(value), tz)
  );
  if (error) {
    return null;
  }
  if (granularity && granularity !== 'day') {
    return zonedDate;
  }
  return new CalendarDate(zonedDate.year, zonedDate.month, zonedDate.day);
}

export function stringify(date: DateValue | null) {
  if (!date) {
    return null;
  }
  if (date instanceof CalendarDate) {
    return date.toString();
  }
  if (date instanceof CalendarDateTime) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toDate(tz).toISOString();
  }
  return date.toDate().toISOString();
}

export function DateField<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  label,
  description,
  horizontal,
  className,
  disabled,
  canHandleField = true,
  allowedMaxDate,
  allowedMinDate,
  onValueChange,
  overrideRequired,
  ...props
}: DateFieldProps<Instance> & { onValueChange?: (value: unknown) => void }) {
  const isRequired = overrideRequired ?? formField.required;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const maxValue = allowedMaxDate
    ? props.granularity === 'minute'
      ? fromDate(allowedMaxDate.toJSDate(), tz)
      : new CalendarDate(
          allowedMaxDate.year,
          allowedMaxDate.month,
          allowedMaxDate.day
        )
    : undefined;
  const minValue = allowedMinDate
    ? props.granularity === 'minute'
      ? fromDate(allowedMinDate.toJSDate(), tz)
      : new CalendarDate(
          allowedMinDate.year,
          allowedMinDate.month,
          allowedMinDate.day
        )
    : undefined;

  if (horizontal)
    return (
      <FormField
        rules={{
          required: isRequired && 'Detta fält är obligatoriskt',
        }}
        control={formField.control}
        name={name as Path<Instance>}
        render={({ field }) => {
          const parsedDate = parse(field.value, props.granularity);

          return (
            <FormItem
              className={cn('flex items-center justify-between', className)}
            >
              <div className='w-1/2 shrink-0'>
                <FormLabel>
                  {label ?? formField.label}{' '}
                  {isRequired && <span>*</span>}
                </FormLabel>
                <FormDescription>
                  {description ?? formField?.description}
                </FormDescription>
                <FormMessage />
              </div>
              <FormControl>
                <DateTimePicker
                  {...field}
                  {...props}
                  isDisabled={disabled ?? field.disabled ?? !canHandleField}
                  label={label ?? formField.label}
                  defaultValue={parsedDate}
                  value={parsedDate}
                  onChange={(v) => {
                    field.onChange(stringify(v));
                    onValueChange?.(stringify(v));
                  }}
                  maxValue={maxValue}
                  minValue={minValue}
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    );

  return (
    <FormField
      rules={{
        required: isRequired && 'Detta fält är obligatoriskt',
      }}
      control={formField.control}
      name={name as Path<Instance>}
      render={({ field }) => {
        const parsedDate = parse(field.value, props.granularity);

        return (
          <FormItem className={className}>
            <div className='flex items-center'>
              <FormLabel>
                {label ?? formField.label}{' '}
                {isRequired && <span>*</span>}
              </FormLabel>
              {(description || formField.description) && (
                <DescriptionTooltip
                  description={description ?? formField.description}
                />
              )}
            </div>
            <FormControl>
              <DateTimePicker
                {...field}
                {...props}
                isDisabled={disabled ?? field.disabled ?? !canHandleField}
                label={label ?? formField.label}
                defaultValue={parsedDate}
                value={parsedDate}
                onChange={(v) => {
                  field.onChange(stringify(v));
                  onValueChange?.(stringify(v));
                }}
                maxValue={maxValue}
                minValue={minValue}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
